/**
 * Localized labels for renderer-embedded placeholder messages.
 * The app supplies these via its i18n layer so the core renderers stay
 * locale-agnostic. All fields are optional; English fallbacks are used
 * when a label is omitted.
 */
export interface EmptyStateLabels {
  /** Title text for the deploy view when no deploy block is defined. */
  deployTitle?: string;
  /** Secondary hint text below the deploy empty-state title. */
  deployHint?: string;
  /** Caption for the deploy view job band wrapper (#1738). */
  deployJobBand?: string;
  /** Caption for the deploy view container holding units with no `realizes`. */
  deployUnclassified?: string;
  /** Text for the org view when no teams have been defined yet. */
  orgNoTeams?: string;
  /** Text for the system view when the layout has no nodes or containers. */
  systemNoNodes?: string;
  /**
   * Text for the org all-layers / drill-down placeholder rendered when the
   * `.krs` file has no organization block (or compiled to no levels).
   */
  orgPlaceholder?: string;
  /**
   * Text for the system all-layers / drill-down placeholder rendered when
   * the `.krs` file produces no system-side root content.
   */
  systemNoDiagram?: string;
  /** Footer line for the team-dependency graph when the join produced no pair (#2636). */
  teamDependencyNone?: string;
  /**
   * Footer line naming how many endpoints reached no team, with `{count}`
   * substituted. Always shown when there are any: the derivation is only as
   * complete as `owns`, and a graph without its remainder reads as the whole
   * model (TPL-2075).
   */
  teamDependencyUnowned?: string;
  /**
   * Footer line naming how many nodes are owned by a team other than the one
   * owning what encloses them, with `{count}` substituted (#2637). No edge
   * crosses that boundary, so the graph cannot draw it — saying nothing would
   * leave the stronger of the two signals invisible on the visual surface.
   */
  teamDependencyOverlap?: string;
}

export const DEFAULT_EMPTY_STATE_LABELS = {
  deployTitle: "No deploy block defined",
  deployHint: "Add a deploy block to your .krs file",
  deployJobBand: "Scheduled jobs",
  deployUnclassified: "Unclassified",
  orgNoTeams: "No teams defined",
  systemNoNodes: "No nodes to render",
  orgPlaceholder: "No org diagram",
  systemNoDiagram: "No diagram",
  teamDependencyNone: "No team dependencies derived",
  teamDependencyUnowned: "{count} endpoint(s) owned by no team",
  teamDependencyOverlap: "{count} node(s) owned across a containment boundary",
} as const satisfies Required<EmptyStateLabels>;
